import React from "react";
import { Box, Checkbox, Chip, Collapse, Divider } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { COLORS } from "../../assets/theme";
import CustomButton from "../CustomButton";
import CustomDialog from "../CustomDialog";
import CustomLabel from "../CustomLabel";
import CustomTextField from "../CustomTextField";

export type ServiceCatalogueEntry = {
  id: string;
  name: string;
  duration: number;
  businessId: string;
  businessName: string;
  unitId: string;
  unitName: string;
};

export interface ServiceScopePickerDialogProps {
  open: boolean;
  onClose: () => void;
  services: ServiceCatalogueEntry[];
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
  loading?: boolean;
  title?: string;
  description?: string;
}

const matchesQuery = (service: ServiceCatalogueEntry, query: string) =>
  !query ||
  [service.name, service.unitName, service.businessName]
    .join(" ")
    .toLowerCase()
    .includes(query);

/**
 * Services are duplicated per unit, so an org runs to dozens of near-identical
 * rows — "60 min - court A", "60 min - court B". A flat multi-select is
 * unusable at that length, so this groups by business and unit, with search
 * and select-all per group.
 */
const ServiceScopePickerDialog: React.FC<ServiceScopePickerDialogProps> = ({
  open,
  onClose,
  services,
  selectedIds,
  onConfirm,
  loading = false,
  title = "Select services",
  description,
}) => {
  const [search, setSearch] = React.useState("");
  const [draft, setDraft] = React.useState<string[]>([]);
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});

  // Re-seed from the saved list each time it opens; edits only stick on Save.
  React.useEffect(() => {
    if (open) {
      setDraft(selectedIds.map(String));
      setSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const query = search.trim().toLowerCase();

  const groups = React.useMemo(() => {
    const byBusiness = new Map<
      string,
      {
        businessId: string;
        businessName: string;
        units: Map<
          string,
          { unitId: string; unitName: string; services: ServiceCatalogueEntry[] }
        >;
      }
    >();

    services
      .filter((service) => matchesQuery(service, query))
      .forEach((service) => {
        const business = byBusiness.get(service.businessId) || {
          businessId: service.businessId,
          businessName: service.businessName,
          units: new Map(),
        };
        const unit = business.units.get(service.unitId) || {
          unitId: service.unitId,
          unitName: service.unitName,
          services: [],
        };
        unit.services.push(service);
        business.units.set(service.unitId, unit);
        byBusiness.set(service.businessId, business);
      });

    return Array.from(byBusiness.values()).map((business) => ({
      ...business,
      units: Array.from(business.units.values()),
    }));
  }, [query, services]);

  const selected = React.useMemo(() => new Set(draft), [draft]);

  const toggleOne = (id: string) =>
    setDraft((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );

  const setMany = (ids: string[], checked: boolean) =>
    setDraft((current) => {
      const next = new Set(current);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return Array.from(next);
    });

  const renderGroupToggle = (
    label: string,
    ids: string[],
    fontSize: number,
    fontWeight: number,
  ) => {
    const allChecked = ids.length > 0 && ids.every((id) => selected.has(id));
    const someChecked = ids.some((id) => selected.has(id));

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Checkbox
          size="small"
          checked={allChecked}
          indeterminate={!allChecked && someChecked}
          onChange={(event) => setMany(ids, event.target.checked)}
          sx={{
            color: COLORS.MainColor,
            "&.Mui-checked": { color: COLORS.MainColor },
          }}
        />
        <CustomLabel
          text={label}
          fontSize={fontSize}
          fontWeight={fontWeight}
          color={COLORS.typographyColor}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        />
      </Box>
    );
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      title={title}
      paperSx={{ maxWidth: 720, width: { xs: "94vw", sm: 720 } }}
      actions={
        <>
          <CustomButton label="Cancel" variant="outlined" onClick={onClose} />
          <CustomButton
            label={`Save${draft.length ? ` (${draft.length})` : ""}`}
            variant="contained"
            onClick={() => onConfirm(draft)}
          />
        </>
      }>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {description && (
          <CustomLabel
            text={description}
            fontSize={13}
            color={COLORS.placeHolderColor}
            sx={{ lineHeight: 1.45 }}
          />
        )}

        <CustomTextField
          label=""
          placeholder="Search service, court or business"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          fullWidth
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}>
          <Chip
            size="small"
            label={`${draft.length} selected`}
            sx={{
              bgcolor: COLORS.SidebarActiveBg,
              color: COLORS.SidebarActiveText,
            }}
          />
          {draft.length > 0 && (
            <CustomButton
              label="Clear all"
              variant="text"
              size="small"
              onClick={() => setDraft([])}
            />
          )}
        </Box>

        <Box
          sx={{
            maxHeight: { xs: "50vh", sm: 420 },
            overflowY: "auto",
            border: `1px solid ${COLORS.BorderColor}`,
            borderRadius: 1,
          }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <CustomLabel
                text="Loading services..."
                fontSize={14}
                color={COLORS.placeHolderColor}
              />
            </Box>
          ) : groups.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <CustomLabel
                text={
                  query ? "No services match that search." : "No services found."
                }
                fontSize={14}
                color={COLORS.placeHolderColor}
              />
            </Box>
          ) : (
            groups.map((business) => {
              const businessIds = business.units.flatMap((unit) =>
                unit.services.map((service) => service.id),
              );
              const isCollapsed = collapsed[business.businessId] === true;

              return (
                <Box key={business.businessId}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1,
                      py: 0.75,
                      bgcolor: COLORS.MainBackground,
                    }}>
                    {renderGroupToggle(
                      business.businessName,
                      businessIds,
                      14,
                      700,
                    )}
                    <Box
                      component="button"
                      type="button"
                      aria-label={
                        isCollapsed ? "Expand business" : "Collapse business"
                      }
                      onClick={() =>
                        setCollapsed((current) => ({
                          ...current,
                          [business.businessId]: !isCollapsed,
                        }))
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: COLORS.SidebarInactiveText,
                        p: 0.5,
                      }}>
                      {isCollapsed ? (
                        <ExpandMoreIcon fontSize="small" />
                      ) : (
                        <ExpandLessIcon fontSize="small" />
                      )}
                    </Box>
                  </Box>

                  <Collapse in={!isCollapsed} unmountOnExit>
                    {business.units.map((unit) => (
                      <Box key={`${business.businessId}-${unit.unitId}`}>
                        <Box sx={{ pl: 2, pr: 1, py: 0.25 }}>
                          {renderGroupToggle(
                            unit.unitName,
                            unit.services.map((service) => service.id),
                            13,
                            600,
                          )}
                        </Box>
                        {unit.services.map((service) => (
                          <Box
                            key={service.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              pl: 4,
                              pr: 1,
                            }}>
                            <Checkbox
                              size="small"
                              checked={selected.has(service.id)}
                              onChange={() => toggleOne(service.id)}
                              sx={{
                                color: COLORS.MainColor,
                                "&.Mui-checked": { color: COLORS.MainColor },
                              }}
                            />
                            <CustomLabel
                              // "service name - unit name" is what tells two
                              // otherwise identical rows apart.
                              text={`${service.name} - ${unit.unitName}${
                                service.duration ? ` · ${service.duration} min` : ""
                              }`}
                              fontSize={13}
                              color={COLORS.typographyColor}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Collapse>
                  <Divider />
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </CustomDialog>
  );
};

export default ServiceScopePickerDialog;
